import React, { useState, useEffect } from "react";
import StoreFinder from "./StoreFinder";

interface Store {
  id: string;
  name: string;
  address_street_1?: string;
  address_street_2?: string;
  city?: string;
  state?: string;
  postcode?: string | number;
  latitude?: number;
  longitude?: number;
  distance?: string | number;
  stock_status?: string;
  bg?: string;
  color?: string;
  clickandcollect_enabled?: number;
  sfs_enabled?: number;
  virtual_enabled?: number;
  deptstore_enabled?: number;
  shopify_location_id?: string;

}

const ClickAndCollect = ({
  isOpen,
  onClose,
  onSelectStore,
  selectedStore,
  displayMode = "status",
  variantIds = [],
  cartCount,
  onStoreResultsFetched,
}) => {
  const [storeResults, setStoreResults] = useState<{ storesByLocation?: Record<string, Store[]> }>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [searchPostcode, setSearchPostcode] = useState("");

  const handleSearch = (query: string) => {
    const postcodeMatch = query.match(/\b\d{4}\b/);
    const matchedPostcode = postcodeMatch ? postcodeMatch[0] : "";
    setSearchPostcode(matchedPostcode);
    setHasSearched(true);
  };

const storesByLocation: Store[] = storeResults?.storesByLocation
  ? Array.from(
      new Map(
        Object.values(storeResults.storesByLocation)
          .flat()
          .filter((store): store is Store => {
            if (
              !store ||
              typeof store !== "object" ||
              !("postcode" in store) ||
              (store as Store).clickandcollect_enabled !== 1
            ) {
              return false;
            }

            // Check stock availability for variantIds
            const variantsAtLocation = storeResults.storesByLocation?.[(store as Store).shopify_location_id] || [];
            const availabilityMap = new Map();
            variantsAtLocation.forEach((variant) => {
              if (variant.shopify_variant_id) {
                availabilityMap.set(String(variant.shopify_variant_id), variant.free_stock ?? 0);
              }
            });

            let availableCount = 0;
            variantIds.forEach((id) => {
              const availableStock = availabilityMap.get(String(id)) ?? 0;
              if (availableStock > 0) availableCount += 1;
            });

            return availableCount > 0;
          })
          .map((store) => [store.id, store])
      ).values()
    )
  : [];

  const handleSelectStore = (store) => {
    onSelectStore(store);
    onClose();
  };

  const locationStatus = (locationId) => {
    const variantsAtLocation = storeResults?.storesByLocation?.[locationId] || [];

    if (!variantIds.length) return "No variants to check";

    const availabilityMap = new Map();
    variantsAtLocation.forEach((variant) => {
      if (variant.shopify_variant_id) {
        availabilityMap.set(String(variant.shopify_variant_id), variant.free_stock ?? 0);
      }
    });

    let availableCount = 0;
    variantIds.forEach((id) => {
      const availableStock = availabilityMap.get(String(id)) ?? 0;
      if (availableStock > 0) availableCount += 1;
    });

    if (availableCount === variantIds.length) {
      return "Available";
    } else if (availableCount > 0) {
      return `${availableCount} of ${variantIds.length} available`;
    } else {
      return `0 of ${variantIds.length} available`;
    }
  };
  
  const renderStoreStatus = (store) => {
    const status = locationStatus(store.shopify_location_id);

    return (
      <div
        className={`flex items-center gap-2 ${
          status === "Available"
            ? "bg-[#DFF5E1] text-[#36573B]"
            : status !== "Unavailable" && status !== "0 of 0 available"
            ? "bg-[#FFF4E5] text-[#FA700C]"
            : "bg-[#FDEDED] text-[#D12727]"
        } p-2 rounded-sm blabel font-medium`}
      >
        <i
          className={`text-2xl icon ${
            status === "Available"
              ? "icon-check-circle"
              : status !== "Unavailable" && status !== "0 of 0 available"
              ? "icon-info"
              : "icon-cross-circle"
          }`}
        ></i>
        {status}
      </div>
    );
  };

  const renderStoreDistance = (store) => (
    <div className="text-sm text-[#54565A]">
      {store.distance} km
    </div>
  );

  useEffect(() => {
    const hasResults =
      storeResults?.storesByLocation &&
      Object.keys(storeResults.storesByLocation).length > 0;
  
    if (!hasResults && storeResults && Object.keys(storeResults).length > 0) {
      setHasSearched(true);
    }

    onStoreResultsFetched(storeResults);
  }, [storeResults]);

  return isOpen ? (
    <div className={`${displayMode === "status" ? "w-full transform -translate-x-1/2 left-1/2" : "left-0"} absolute z-10 bg-white border border-[#BBBBBD] shadow-[0px_4px_11px_0px_rgba(84,86,90,0.10)] top-full`}>
      {displayMode === "status" && (
        <div className="absolute top-0 w-5 h-5 rotate-45 -translate-x-1/2 -translate-y-1/2 bg-white border-t border-l -m-[1px] border-[#BBBBBD] left-[74%]"></div>
      )}
      <div className={`${displayMode === "status" ? "w-full" : ""} px-6 py-8`}>
        <h4 className="mb-6 !text-base/[22px] text-[#54565A] lg:!text-[21px]/6">
          {displayMode === "distance" ? "Set My Store" : "Select a store"}
        </h4>

        <StoreFinder
          layout="default"
          variantIds={variantIds}
          onStoresFetched={setStoreResults}
          onSearch={handleSearch}
        />

        {storesByLocation.length > 0 ? (
          <ul className="p-0 space-y-2 store-list lg:space-y-3">
            {storesByLocation.map((store) => (
              <li
                key={store.id}
                className="flex items-center justify-between border-b border-[#ECEEEF] last:border-b-transparent cursor-pointer pb-2 lg:pb-3"
                onClick={() => handleSelectStore(store)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-[#23263B] rounded-full flex items-center justify-center">
                    {selectedStore?.id === store.id && (
                      <div className="w-2.5 h-2.5 bg-[#23263B] rounded-full"></div>
                    )}
                  </div>
                  <span className="text-[#54565A] text-base/[22px] font-normal">
                    {store.name}
                  </span>
                </div>
                {displayMode === "status" && renderStoreStatus(store)}
                {displayMode === "distance" && renderStoreDistance(store)}
              </li>
            ))}
          </ul>
        ) : (
          displayMode === "status" && (
            <div className="flex flex-col items-center justify-center py-6 text-center text-black">
              {hasSearched ? (
                <>
                  <span className="text-[#54565A] text-base">No stores available.</span>
                </>
              ) : (
                <>
                  <span className="mt-6 mb-3">
                    ... <i className="text-2xl icon icon-store"></i>
                  </span>
                  <span className="text-[#54565A] text-lg/6">Select a store for Click & Collect</span>
                </>
              )}
            </div>
          )
        )}
      </div>
    </div>
  ) : null;
};

export default ClickAndCollect;